import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getAccounts, createAccount, updateAccount } from '@/services/firestore'
import { Landmark, Plus, Save, RefreshCw, X } from 'lucide-react'

export const Route = createFileRoute('/admin/accounts')({ component: AccountsPage })

function AccountsPage() {
    const [accounts, setAccounts] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({ code: '', name: '', type: 'asset' as string, description: '', openingBalance: 0 })
    const [saving, setSaving] = useState(false)

    useEffect(() => { load() }, [])
    const load = async () => {
        setLoading(true)
        const data = await getAccounts()
        setAccounts(data)
        setLoading(false)
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            if (editingId) {
                await updateAccount(editingId, { openingBalance: form.openingBalance, name: form.name, description: form.description })
            } else {
                await createAccount(form)
            }
            await load()
            setShowModal(false)
            setEditingId(null)
            setForm({ code: '', name: '', type: 'asset', description: '', openingBalance: 0 })
        } finally { setSaving(false) }
    }

    const openEdit = (acc: any) => {
        setForm({ code: acc.code, name: acc.name, type: acc.type, description: acc.description || '', openingBalance: acc.openingBalance || 0 })
        setEditingId(acc.id)
        setShowModal(true)
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })
    const typeColors: Record<string, string> = {
        asset: 'bg-blue-50 text-blue-700', liability: 'bg-red-50 text-red-700', equity: 'bg-purple-50 text-purple-700',
        revenue: 'bg-green-50 text-green-700', expense: 'bg-orange-50 text-orange-700',
    }

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Landmark className="w-6 h-6 text-blue-500" />Chart of Accounts</h1>
                    <p className="text-xs text-slate-500 mt-0.5">Manage your accounts and opening balances</p>
                </div>
                <button onClick={() => { setEditingId(null); setForm({ code: '', name: '', type: 'asset', description: '', openingBalance: 0 }); setShowModal(true) }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />New Account
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Code</th>
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Account Name</th>
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Type</th>
                                <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Opening Balance</th>
                                <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Current Balance</th>
                                <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map((acc, i) => (
                                <tr key={acc.id} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-25'}`}>
                                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">{acc.code}</td>
                                    <td className="px-4 py-3 font-semibold text-slate-700">{acc.name}</td>
                                    <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${typeColors[acc.type] || ''}`}>{acc.type}</span></td>
                                    <td className="px-4 py-3 text-right font-mono text-slate-600">{f(acc.openingBalance || 0)}</td>
                                    <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">{f(acc.currentBalance || 0)}</td>
                                    <td className="px-4 py-3 text-center">
                                        <button onClick={() => openEdit(acc)} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">Edit</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-slate-800">{editingId ? 'Edit Account' : 'New Account'}</h2>
                            <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            {!editingId && (
                                <>
                                    <div>
                                        <label className="text-xs font-bold text-slate-600 uppercase">Account Code</label>
                                        <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g. 5007" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-slate-600 uppercase">Type</label>
                                        <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50">
                                            <option value="asset">Asset</option><option value="liability">Liability</option><option value="equity">Equity</option><option value="revenue">Revenue</option><option value="expense">Expense</option>
                                        </select>
                                    </div>
                                </>
                            )}
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Account Name</label>
                                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Opening Balance (PKR)</label>
                                <input type="number" value={form.openingBalance} onChange={e => setForm({ ...form, openingBalance: parseFloat(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Description</label>
                                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" />
                            </div>
                            <button onClick={handleSave} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{saving ? 'Saving...' : 'Save Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
